import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, fmtEUR, API, BACKEND_URL, mediaUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ShoppingCart, Minus, Plus, Package, PlusCircle, MinusCircle, MoveRight, MapPin, Printer } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { PlaceStockModal, AdjustStockModal, MoveStockModal } from "@/components/StockModals";
import { formatLocDisplay, formatLocString } from "@/components/WarehouseExtras";

// --- HTML escape helper ---
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function openTicketsWindow(product, locations) {
  const article = product.article_number || product.stock_article_number || product.sku || product.id;
  const qrUrl = (data, size = 8) => `${BACKEND_URL}/api/admin/qr?data=${encodeURIComponent(data)}&size=${size}`;
  const placementTickets = locations.length > 0 ? locations.map(l => {
    const code = `HGP|${article}|${l.cabinet}|${l.drawer}|${l.position}`;
    return `
      <div class="ticket ticket-place">
        <div class="type-tag">STOK PLAS ${esc(l.cabinet)}-${esc(l.drawer)}-${esc(l.position)}</div>
        <div class="tk-body">
          <img src="${qrUrl(code, 10)}" class="qr" />
          <div class="tk-info">
            <div class="tk-loc">📦 ${esc(l.cabinet)}-${esc(l.drawer)}-${esc(l.position)}</div>
            <div class="tk-title">${esc(product.name)}</div>
            <div class="tk-art">Art. Nr: <b>${esc(article)}</b></div>
            <div class="tk-hint">Skano për të shtuar stok në këtë vend</div>
          </div>
        </div>
      </div>
    `;
  }).join("") : `
    <div class="ticket ticket-place">
      <div class="type-tag">STOK PLAS (pa lokacion caktuar)</div>
      <div class="tk-body">
        <img src="${qrUrl(`HGR|${article}`, 10)}" class="qr" />
        <div class="tk-info">
          <div class="tk-title">${esc(product.name)}</div>
          <div class="tk-art">Art. Nr: <b>${esc(article)}</b></div>
          <div class="tk-hint">Ky produkt s'ka lokacion — cakto një lokacion fillimisht</div>
        </div>
      </div>
    </div>
  `;
  const removeCode = `HGR|${article}`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`
    <!doctype html><html><head><title>Tiketa - ${esc(product.name)}</title>
    <style>
      @page { size: A4; margin: 10mm; }
      body { font-family: Arial, sans-serif; margin: 0; padding: 10px; background: white; color: black; }
      .no-print { padding: 8px 16px; background: #FFD600; border: 0; font-weight: bold; cursor: pointer; margin-bottom: 12px; }
      @media print { .no-print { display: none; } }
      .ticket { border: 3px dashed #000; margin-bottom: 12px; page-break-inside: avoid; }
      .ticket-place { border-color: #059669; }
      .ticket-remove { border-color: #dc2626; }
      .type-tag { padding: 8px 12px; font-size: 12pt; font-weight: bold; letter-spacing: 1px; color: #fff; }
      .ticket-place .type-tag { background: #059669; }
      .ticket-remove .type-tag { background: #dc2626; }
      .tk-body { display: flex; gap: 16px; padding: 16px; align-items: center; }
      .qr { width: 200px; height: 200px; }
      .tk-info { flex: 1; }
      .tk-loc { font-size: 20pt; font-family: monospace; font-weight: bold; color: #059669; margin-bottom: 6px; }
      .tk-title { font-size: 15pt; font-weight: bold; margin-bottom: 4px; }
      .tk-art { font-size: 12pt; margin-bottom: 4px; }
      .tk-hint { font-size: 9pt; color: #666; font-style: italic; }
    </style></head><body>
    <button class="no-print" onclick="window.print()">Printo</button>
    ${placementTickets}
    <div class="ticket ticket-remove">
      <div class="type-tag">DORËZIM • Artikull ${esc(article)}</div>
      <div class="tk-body">
        <img src="${qrUrl(removeCode, 10)}" class="qr" />
        <div class="tk-info">
          <div class="tk-title">${esc(product.name)}</div>
          <div class="tk-art">Art. Nr: <b>${esc(article)}</b></div>
          <div class="tk-hint">Skano për të nxjerrë stokun nga magazina</div>
        </div>
      </div>
    </div>
    <script>
      window.addEventListener('load', function() {
        const imgs = document.querySelectorAll('img');
        let loaded = 0;
        if (imgs.length === 0) { setTimeout(() => window.print(), 300); return; }
        imgs.forEach(img => {
          const done = () => { loaded++; if (loaded === imgs.length) setTimeout(() => window.print(), 300); };
          if (img.complete) done(); else { img.addEventListener('load', done); img.addEventListener('error', done); }
        });
      });
    </script>
    </body></html>
  `);
  w.document.close();
}

const stockLabel = { in: "Në stok", low: "Pak në stok", out: "Nuk ka në stok", backorder: "Me porosi" };
const stockColor = { in: "text-emerald-500", low: "text-orange-400", out: "text-red-500", backorder: "text-blue-400" };

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [stock, setStock] = useState(null);
  const [modal, setModal] = useState(null); // "place" | "add" | "remove" | "move" | null
  const { addToCart, user } = useApp();
  const isAdmin = user?.role === "admin";

  const loadStock = useCallback(async () => {
    if (!isAdmin || !p?.id) return;
    try {
      const r = await api.get(`/admin/products/${p.id}/stock`);
      setStock(r.data);
    } catch {}
  }, [isAdmin, p?.id]);

  useEffect(() => { api.get(`/products/${id}`).then(r => setP(r.data)); }, [id]);
  useEffect(() => { loadStock(); }, [loadStock]);

  if (!p) return <div className="p-8 text-center text-neutral-400">Duke u ngarkuar...</div>;

  const price = p.discount_price || p.price;
  const img = p.images?.[0];
  const add = () => {
    addToCart({ product_id: p.id, name: p.name, quantity: qty, unit_price: price, vat_rate: p.vat_rate ?? 18, image: img });
    toast.success("U shtua në shportë");
  };
  const onStockChange = () => { loadStock(); api.get(`/products/${id}`).then(r => setP(r.data)); setModal(null); };
  const totalQty = stock?.total_qty ?? p.stock_qty;
  const locs = stock?.locations || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="grid md:grid-cols-2 gap-6 md:gap-10">
        <div className="hg-card p-4 bg-white">
          <div className="aspect-square bg-white overflow-hidden">
            <img src={img} alt={p.name} className="w-full h-full object-contain" />
          </div>
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase mb-4">{p.name}</h1>
          <div className={`flex items-center gap-2 mb-6 text-sm ${stockColor[p.stock_status]}`}>
            <span className="w-2 h-2 rounded-full bg-current" />
            {stockLabel[p.stock_status]}{isAdmin && <> • {p.stock_qty} {p.unit}</>}
          </div>
          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-display text-5xl text-[#FFD600]">{fmtEUR(price)}</span>
            {p.discount_price && <span className="text-neutral-500 line-through">{fmtEUR(p.price)}</span>}
            <span className="text-xs text-neutral-400 uppercase">/ {p.unit}</span>
          </div>
          <p className="text-neutral-300 mb-6">{p.description}</p>

          <div className="grid grid-cols-2 gap-2 text-xs mb-6 hg-card p-4">
            {p.brand && <><div className="text-neutral-500 uppercase">Marka</div><div>{p.brand}</div></>}
            {p.model && <><div className="text-neutral-500 uppercase">Materiali</div><div>{p.model}</div></>}
            {p.size && <><div className="text-neutral-500 uppercase">Madhësia</div><div>{p.size}</div></>}
            {p.size_code && <><div className="text-neutral-500 uppercase">Kodi / Modeli</div><div>{p.size_code}</div></>}
            {p.sku && <><div className="text-neutral-500 uppercase">Made in</div><div>{p.sku}</div></>}
            {p.article_number && <><div className="text-neutral-500 uppercase">Artikulli</div><div>{p.article_number}</div></>}
            {p.oem_number && <><div className="text-neutral-500 uppercase">OEM</div><div>{p.oem_number}</div></>}
            {Object.entries(p.specifications || {}).map(([k, v]) => (
              <div key={k} className="contents"><div className="text-neutral-500 uppercase">{k}</div><div>{v}</div></div>
            ))}          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center border border-[#1F2B44]">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12 flex items-center justify-center hover:bg-[#262626]"><Minus size={16} /></button>
              <div className="w-12 text-center font-bold">{qty}</div>
              <button data-testid="qty-plus" onClick={() => {
                const max = p.stock_qty ?? 0;
                if (!isAdmin && p.stock_status !== "backorder" && qty >= max) {
                  toast.error(`Nuk ka më sasi. Maksimumi ${max} ${p.unit || "copë"}.`);
                  return;
                }
                setQty(qty + 1);
              }} className="w-11 h-12 flex items-center justify-center hover:bg-[#262626]"><Plus size={16} /></button>
            </div>
            <button data-testid="add-to-cart-detail" onClick={add} disabled={p.stock_status === "out"}
              className="hg-btn-primary flex-1 h-12 uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
              <ShoppingCart size={18} /> Shto në shportë
            </button>
          </div>
        </div>
      </div>

      {/* ================= ADMIN STOCK PANEL ================= */}
      {isAdmin && (
        <div className="mt-10 border-t border-[#1F2B44] pt-8" data-testid="admin-stock-panel">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="hg-card p-5">
              <div className="text-xs uppercase text-neutral-400 tracking-widest mb-2">Stoku aktual</div>
              <div className="font-display text-5xl text-emerald-400" data-testid="stock-total">{totalQty}</div>
              <div className="text-sm text-neutral-400">copë</div>
            </div>
            <div className="hg-card p-5 md:col-span-2">
              <div className="text-xs uppercase text-neutral-400 tracking-widest mb-3 flex items-center gap-2">
                <MapPin size={14} /> Lokacionet ({locs.length})
              </div>
              {locs.length === 0 ? (
                <div className="text-neutral-500 text-sm">Nuk ka lokacion. Përdor "Vendos në stok".</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  {locs.map((l, i) => (
                    <div className="flex items-center justify-between border border-[#1F2B44] px-3 py-2" data-testid={`stock-loc-${i}`}>
                      <div className="font-mono">
                        <span className="text-[#FFD600] font-bold">{l.cabinet}-{l.drawer}-{l.position}</span>
                      </div>
                      <div className="text-emerald-400 font-bold">{l.qty} copë</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => setModal("place")} data-testid="btn-place-stock"
              className="hg-btn-primary px-4 py-3 uppercase text-xs inline-flex items-center gap-2">
              <Package size={16} /> Vendos në stok
            </button>
            <button onClick={() => setModal("add")} disabled={locs.length === 0} data-testid="btn-add-stock"
              className="border-2 border-emerald-600 text-emerald-400 px-4 py-3 uppercase text-xs inline-flex items-center gap-2 hover:bg-emerald-900/40 disabled:opacity-30 disabled:cursor-not-allowed">
              <PlusCircle size={16} /> Shto stok
            </button>
            <button onClick={() => setModal("remove")} disabled={locs.length === 0} data-testid="btn-remove-stock"
              className="border-2 border-neutral-700 text-neutral-300 px-4 py-3 uppercase text-xs inline-flex items-center gap-2 hover:bg-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed">
              <MinusCircle size={16} /> Nxirr nga stoku
            </button>
            <button onClick={() => setModal("move")} disabled={locs.length === 0} data-testid="btn-move-stock"
              className="border-2 border-[#FFD600] text-[#FFD600] px-4 py-3 uppercase text-xs inline-flex items-center gap-2 hover:bg-[#FFD600]/10 disabled:opacity-30 disabled:cursor-not-allowed">
              <MoveRight size={16} /> Zhvendos
            </button>
            <button onClick={() => openTicketsWindow(p, locs)} data-testid="btn-print-tickets"
              className="border-2 border-white text-white px-4 py-3 uppercase text-xs inline-flex items-center gap-2 hover:bg-white/10">
              <Printer size={16} /> Printo Tiketa QR
            </button>
          </div>

          {/* Movement history */}
          {stock?.movements?.length > 0 && (
            <div className="hg-card p-4 overflow-x-auto">
              <div className="font-display text-lg uppercase mb-3">Historiku i lëvizjeve për këtë produkt</div>
              <table className="w-full text-xs" data-testid="stock-history">
                <thead>
                  <tr className="text-neutral-500 uppercase text-[10px] tracking-widest">
                    <th className="text-left py-2 pr-3">Data / Ora</th>
                    <th className="text-left py-2 pr-3">Veprimi</th>
                    <th className="text-left py-2 pr-3">Sasia</th>
                    <th className="text-left py-2 pr-3">Nga</th>
                    <th className="text-left py-2 pr-3">Në</th>
                    <th className="text-left py-2 pr-3">Përdoruesi</th>
                    <th className="text-left py-2 pr-3">Shënim</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.movements.map(m => (
                    <tr key={m.id} className="border-t border-[#1a1a1a]">
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(m.created_at).toLocaleString("sq-AL")}</td>
                      <td className="py-2 pr-3 font-bold uppercase text-[10px]">
                        {m.type === "SET" && <span className="text-[#FFD600]">Vendosje</span>}
                        {m.type === "IN" && <span className="text-emerald-400">Hyrje</span>}
                        {m.type === "OUT" && <span className="text-red-400">Dalje</span>}
                        {m.type === "MOVE" && <span className="text-blue-400">Zhvendosje</span>}
                      </td>
                      <td className="py-2 pr-3">
                        {m.type === "OUT" ? "−" : m.type === "MOVE" ? "" : "+"}{m.qty}
                      </td>
                      <td className="py-2 pr-3 font-mono">{m.from_location ? `${m.from_location.cabinet}-${m.from_location.drawer}-${m.from_location.position}` : "—"}</td>
                      <td className="py-2 pr-3 font-mono">{m.to_location ? `${m.to_location.cabinet}-${m.to_location.drawer}-${m.to_location.position}` : "—"}</td>
                      <td className="py-2 pr-3">{m.user_email}</td>
                      <td className="py-2 pr-3 text-neutral-400">{m.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {p.related?.length > 0 && (
        <div className="mt-12">
          <div className="font-display text-2xl uppercase mb-4">Për këtë produkt mund t'ju duhen edhe:</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {p.related.map(r => <ProductCard key={r.id} p={r} />)}
          </div>
        </div>
      )}

      {modal === "place" && <PlaceStockModal productId={p.id} productName={p.name} onClose={() => setModal(null)} onDone={onStockChange} />}
      {modal === "add" && <AdjustStockModal productId={p.id} mode="add" locations={locs} onClose={() => setModal(null)} onDone={onStockChange} />}
      {modal === "remove" && <AdjustStockModal productId={p.id} mode="remove" locations={locs} onClose={() => setModal(null)} onDone={onStockChange} />}
      {modal === "move" && <MoveStockModal productId={p.id} locations={locs} onClose={() => setModal(null)} onDone={onStockChange} />}
    </div>
  );
}
